import { useEffect, useState } from "react";
import { Bar, Line } from "react-chartjs-2";
import axios from "axios";
import { getAuthToken, getChartData, setChartData } from "../../utils/cookieUtils";

const DashboardCharts = ({ adminProfile, monthsOfYear }) => {
  const [barChartData, setBarChartData] = useState(null);
  const [lineChartData, setLineChartData] = useState(null);
  const [currentMonthAttendance, setCurrentMonthAttendance] = useState(0);
  const [currentFullMonthName, setCurrentFullMonthName] = useState("");

  useEffect(() => {
    // Don't fetch data if adminProfile is not loaded yet
    if (!adminProfile || !adminProfile.role) {
      return;
    }

    const currentDate = new Date();
    const token = getAuthToken();

    const isSuperAdmin = adminProfile?.role?.id === 3;

    // Check cache based on admin role
    const cachedLineChartData = getChartData("lineChartData");
    const cachedBarChartData = isSuperAdmin ? getChartData("barChartData") : null;

    if (cachedLineChartData && (!isSuperAdmin || cachedBarChartData)) {
      setLineChartData(cachedLineChartData);
      if (isSuperAdmin) {
        setBarChartData(cachedBarChartData);
      }
      setCurrentMonthAttendance(
        cachedLineChartData.datasets[0].data[currentDate.getMonth()]
      );
      setCurrentFullMonthName(monthsOfYear[currentDate.getMonth()]);
      return;
    }

    const attendancePromises = monthsOfYear.map(async (_, monthIndex) => {
      const startOfMonth = new Date(currentDate.getFullYear(), monthIndex, 1);
      const endOfMonth = new Date(currentDate.getFullYear(), monthIndex + 1, 0);
      const formattedStartDate = startOfMonth.toISOString().split("T")[0];
      const formattedEndDate = endOfMonth.toISOString().split("T")[0];

      try {
        const attendanceResponse = await axios.get(
          `/super-admin/filter-attendance?start_date=${formattedStartDate}&end_date=${formattedEndDate}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        const attendanceResult = attendanceResponse.data.message;
        return attendanceResult.length;
      } catch (error) {
        console.error(
          `Error fetching attendance data for ${
            startOfMonth.getMonth() + 1
          }/${startOfMonth.getFullYear()}:`,
          error
        );
        return 0;
      }
    });

    // Only fetch revenue data if user is a super admin
    const revenuePromises = isSuperAdmin 
      ? monthsOfYear.map(async (_, monthIndex) => {
          const startOfMonth = new Date(currentDate.getFullYear(), monthIndex, 1);
          const endOfMonth = new Date(currentDate.getFullYear(), monthIndex + 1, 0);
          const formattedStartDate = startOfMonth.toISOString().split("T")[0];
          const formattedEndDate = endOfMonth.toISOString().split("T")[0];

          try {
            const revenueResponse = await axios.get(
              `/super-admin/filter-revenue-sum?start_date=${formattedStartDate}&end_date=${formattedEndDate}`,
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                },
              }
            );
            return revenueResponse.data.message.revenue || 0;
          } catch (error) {
            console.error(
              `Error fetching revenue data for ${
                startOfMonth.getMonth() + 1
              }/${startOfMonth.getFullYear()}:`,
              error
            );
            return 0;
          }
        })
      : [];

    const fetchData = async () => {
      try {
        const attendanceData2 = await Promise.all(attendancePromises);
        const revenueData = isSuperAdmin ? await Promise.all(revenuePromises) : [];

        const lineChartData = {
          labels: monthsOfYear,
          datasets: [
            {
              label: "Number of members present",
              data: attendanceData2,
              backgroundColor: (context) => {
                const { chart } = context;
                const { ctx, chartArea } = chart;
                if (!chartArea) {
                  return null;
                }
                const gradient = ctx.createLinearGradient(
                  0,
                  chartArea.top,
                  0,
                  chartArea.bottom
                );
                gradient.addColorStop(0, "rgba(42, 111, 241, 0.4)");
                gradient.addColorStop(1, "rgba(42, 111, 241, 0)");
                return gradient;
              },
              borderColor: "rgba(42, 111, 241, 1)",
              borderWidth: 2,
              fill: true,
              tension: 0.4,
            },
          ],
        };

        // Only create bar chart data for super admins
        const barChartData = isSuperAdmin ? {
          labels: monthsOfYear,
          datasets: [
            {
              label: "Generated Revenue",
              data: revenueData,
              backgroundColor: "rgba(42, 111, 241, 1)",
              borderColor: "rgba(54, 162, 235, 1)",
              borderWidth: 1,
              borderRadius: 10,
            },
          ],
        } : null;

        setLineChartData(lineChartData);
        if (isSuperAdmin) {
          setBarChartData(barChartData);
          setChartData("barChartData", barChartData);
        }
        setChartData("lineChartData", lineChartData);
        setCurrentMonthAttendance(attendanceData2[currentDate.getMonth()]);
        setCurrentFullMonthName(monthsOfYear[currentDate.getMonth()]);
      } catch (error) {
        console.error("Error fetching monthly data:", error);
      }
    };

    fetchData();
  }, [monthsOfYear, adminProfile]);

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        align: "end",
        labels: {
          padding: 20,
          usePointStyle: true,
          pointStyle: "circle",
        },
      },
    },
  };

  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        min: 0,
      },
    },
    plugins: {
      legend: {
        position: "top",
        align: "end",
        labels: {
          padding: 20,
          usePointStyle: true,
          pointStyle: "circle",
        },
      },
    },
  };

  return (
    <div className="graphs">
      {adminProfile?.role?.id === 3 ? (
        <>
          <div className="bar-graph">
            <div className="revenue-header">
              <img
                src={`${process.env.PUBLIC_URL}/images/total-revenue-img.png`}
                alt="Total revenue"
              />
              <div className="revenue-text">
                <p>Total Revenue generated</p>
                <h2>-</h2>
              </div>
            </div>
            <div className="bar-chart-container container-fluid">
              {barChartData ? (
                <Bar data={barChartData} options={barChartOptions} />
              ) : (
                <div className="spinner" style={{ marginTop: "50px" }}></div>
              )}
            </div>
          </div>

          <div className="line-chart container-fluid">
            <div className="attendance-head">
              <img
                src={`${process.env.PUBLIC_URL}/images/total-attendance-img.png`}
                alt="Total attendance"
              />
              <div className="attendance-text2">
                <p>Total Attendance in {currentFullMonthName}</p>
                <h2>
                  {currentMonthAttendance}{" "}
                  <span
                    style={{
                      color: "#666666",
                      fontSize: "14px",
                      fontWeight: "normal",
                    }}
                  >
                    Members
                  </span>
                </h2>
              </div>
            </div>
            <div className="line-chart-container">
              {lineChartData ? (
                <Line
                  id="lineChart"
                  data={lineChartData}
                  options={lineChartOptions}
                />
              ) : (
                <div className="spinner" style={{ marginTop: "50px" }}></div>
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="line-chart container-fluid">
          <div className="attendance-head">
            <img
              src={`${process.env.PUBLIC_URL}/images/total-attendance-img.png`}
              alt="Total attendance"
            />
            <div className="attendance-text2">
              <p>Total Attendance in {currentFullMonthName}</p>
              <h2>
                {currentMonthAttendance}{" "}
                <span
                  style={{
                    color: "#666666",
                    fontSize: "14px",
                    fontWeight: "normal",
                  }}
                >
                  Members
                </span>
              </h2>
            </div>
          </div>
          <div className="line-chart-container">
            {lineChartData ? (
              <Line
                id="lineChart"
                data={lineChartData}
                options={lineChartOptions}
              />
            ) : (
              <div className="spinner" style={{ marginTop: "50px" }}></div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardCharts;
